import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Modal,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { fade } from "@material-ui/core/styles/colorManipulator";

import AddIcon from "@material-ui/icons/AddOutlined";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/SearchOutlined";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import { useLocation, useParams } from "react-router-dom";
import uuidv4 from "uuid/v4";
import { useLocalStorage } from "react-use";

import AnimateHeight from "react-animate-height";

import { useHistory } from "react-router";

import { useDispatch, useSelector } from "react-redux";

import AutocompleteField from "./AutocompleteField";

import {
  SettingsIcon,
  UploadToCloudIcon,
} from "./components/icons/themes/windows10";
import {
  allDestinationsData,
  destinationIsWarehouse,
  destinationKeyToSlugMap,
  destinationSupportsBackfill,
} from "./destinations/destinations";
import FilterList from "./FilterList";
import Navigation from "./Navigation.js";
import { iconForEventType, makeRequest } from "./util";
import {
  allEventTypes,
  destinationIsValidForEvent,
  eventCategories,
  eventDefType,
  eventSourceForEventType,
  isCustomEventType,
  libraryForEventType,
} from "./eventTypes";
import { LOAD_EVENTS } from "./reducers/events";
import { fetchEventDefs } from "./actions/eventDefs";
import onEnvironmentChange from "./hooks/on-environment-change-by-user";

const EVENTS_PAGE_BASE = "/events/schema";

const useTopbarStyles = makeStyles((theme) => ({
  topbar: {
    display: "flex",
    backgroundColor: "transparent",
    width: "100%",
    flex: "0 1 auto",
    height: 70,
  },
  icon: {
    display: "flex",
    // @ts-ignore
    marginRight: theme.spacing(1),
  },
  searchWrapper: {
    width: 275,
    borderBottom: "2px solid rgb(63 63 68 / 15%)",
  },
  searchField: {},
}));

let EventsTopbar = ({
  searchString,
  onSearchStringChange,
  onNewEventDef,
  editorState,
  unsavedEdits,
}) => {
  const classes = useTopbarStyles();

  const matchingCategory = _.find(
    eventCategories,
    (cat) => cat.type === editorState.type
  );

  const headline = matchingCategory
    ? `${matchingCategory.name} / ${editorState.name}`
    : editorState.name;

  return (
    <div className={clsx(classes.topbar)}>
      <div
        className={clsx(
          "bg-white p-4 flex justify-center items-center MuiPaper-elevation1",
          classes.searchWrapper
        )}
      >
        <TextField
          className="w-full"
          variant="outlined"
          name="search"
          autoComplete="off"
          onChange={(e) => onSearchStringChange(e.target.value)}
          value={searchString}
          InputProps={{
            className: clsx(classes.searchField),
            placeholder: "Search events",
            startAdornment: (
              <InputAdornment position={"start"}>
                <SearchIcon className={clsx(classes.icon)} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div
        className={clsx(
          "px-5 py-4 items-center truncate font-bold text-gray-700 flex-grow"
        )}
      >
        <span className="text-2xl">{headline}</span>
      </div>

      <div className={clsx("p-3 flex justify-center items-center")}>
        {!unsavedEdits && (
          <Button
            className="h-full"
            color="primary"
            onClick={() => onNewEventDef()}
            variant="contained"
          >
            <AddIcon className={classes.icon} />
            Create New Event
          </Button>
        )}
      </div>
    </div>
  );
};

EventsTopbar.propTypes = {
  searchString: PropTypes.string,
  onSearchStringChange: PropTypes.func,
  onNewEventDef: PropTypes.func,
  editorState: PropTypes.any,
  unsavedEdits: PropTypes.bool,
};
EventsTopbar = React.memo(EventsTopbar);

const useStyles = makeStyles(() => ({
  content: {
    display: "flex",
    flex: "1 1 auto",
    overflow: "hidden",
  },
  widget: {},
  widgets: {
    overflowY: "auto",
    paddingTop: 1,
    margin: 0,
  },
}));

// optionsForLibrary returns the list of option components for the event type
// dropdown.
function optionsForLibrary(library) {
  if (library === "web") {
    return (
      <>
        <option value="click">Click</option>
        <option value="pageview">Pageview</option>
        <option value="submit">Form Submit</option>
        <option value="change">Change</option>
      </>
    );
  } else if (library === "react-native") {
    return (
      <>
        <option value="touch">Touch</option>
        <option value="text_edit">Edit Input</option>
        <option value="toggle">Toggle Switch</option>
        <option value="react_navigation_screenview">Screen View</option>
      </>
    );
  } else {
    return <></>;
  }
}

function EventsPage() {
  const classes = useStyles();
  const alert = useAlert();
  const location = useLocation();
  const urlParams = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [searchString, setSearchString] = useState("");

  const [enabledDests, setEnabledDests] = useState(null);
  const [disabledBackfills, setDisabledBackfills] = useState(null);
  const [eventEditorState, setEventEditorState] = useState({});
  const [unsavedEdits, setUnsavedEdits] = useState(false);
  const [deleteEventModalOpen, setDeleteEventModalOpen] = useState(false);
  const [
    destinationSettingsModalOpen,
    setDestinationSettingsModalOpen,
  ] = useState(false);

  const destinationMap = useSelector((state) => state.destinations.data);
  const destinations = useMemo(() => Object.values(destinationMap), [
    destinationMap,
  ]);
  const { eventDefs, loading: eventDefsLoading } = useSelector(
    (state) => state.eventDefs
  );
  const events = useMemo(
    () => _.sortBy(eventDefs, (eventDef) => eventDef.name.toLowerCase()),
    [eventDefs]
  );
  const setEvents = useCallback(
    (data) => dispatch({ type: LOAD_EVENTS, payload: data }),
    [dispatch]
  );

  // KLUDGE: When changing the event definition settings, we want to immediately
  // change the event definition state, then trigger a save. We can't call
  // setState then make a request because the setState wouldn't have propagated
  // yet. Instead, we call setTriggerSaveEvent which will save the event the
  // next time through.
  const [triggerSaveEvent, setTriggerSaveEvent] = useState(false);

  // This can be either 'create' or 'edit' depending on whether we are creating
  // a new event or editing an old one.
  const [editorType, setEditorType] = useState(null);

  const handleClickNewEventDef = useCallback(
    () => history.replace(`${EVENTS_PAGE_BASE}/new`),
    [history]
  );

  const handleNewEventDefinition = useCallback(
    (type = "click", typeSpecificProperty = "") => {
      setEnabledDests({});
      setDisabledBackfills({});
      setUnsavedEdits(true);
      setEventEditorState({
        id: uuidv4(),
        name: "",
        typeSpecificFilterVal: typeSpecificProperty,
        type: type,
        filters: [],
        snapshots: [],
        segmentCompatible: false,
      });
      setEditorType("create");
    },
    []
  );

  const handleClickEvent = useCallback(
    ({ id }) => {
      history.push(`${EVENTS_PAGE_BASE}/${id}`);
    },
    [history]
  );

  // Select the given event from the event list.
  const handleSelectEvent = useCallback((event) => {
    const eventType = eventDefType(event);
    var filters = event.filter.filters;

    var typeSpecificFilter;
    if (eventType === "pageview") {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$pathname"
      );
    } else if (eventType === "react_navigation_screenview") {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$screen_path"
      );
    } else {
      typeSpecificFilter = filters.find(
        (filter) => filter.prop === "$elements"
      );
    }

    filters = filters.filter(
      (filter) => filter.prop !== "$event_type" && filter !== typeSpecificFilter
    );
    const editorState = {
      ...event,
      typeSpecificFilterVal: typeSpecificFilter && typeSpecificFilter.val,
      type: eventDefType(event),
      filters: filters,
      snapshots: event.snapshots || [],
      segmentCompatible: event.segment_compatible || false,
    };

    setEventEditorState(editorState);
    setUnsavedEdits(false);
    setEnabledDests(event.enabled_dests);
    setDisabledBackfills({});
    setEditorType("edit");
  }, []);

  // Returns the type specific filter for the event current in the event def
  // editor.
  function editorStateTypeSpecificFilter() {
    if (eventEditorState.type === "pageview") {
      return {
        type: "prop_filter",
        prop: "$pathname",
        op: "wildcard_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    } else if (eventEditorState.type === "react_navigation_screenview") {
      return {
        type: "prop_filter",
        prop: "$screen_path",
        op: "wildcard_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    } else {
      return {
        type: "prop_filter",
        prop: "$elements",
        op: "css_matches",
        val: eventEditorState.typeSpecificFilterVal,
      };
    }
  }

  // Takes the current editor state and converts it into an event definition.
  function editorStateToEventDef() {
    var filters;

    if (isCustomEventType(eventEditorState.type)) {
      // For custom events, there isn't an $event_type filter or event type
      // specific filter so we just included the filters as is.
      filters = eventEditorState.filters;
    } else {
      filters = [
        {
          type: "prop_filter",
          prop: "$event_type",
          op: "=",
          val: eventEditorState.type,
        },
        editorStateTypeSpecificFilter(),
      ].concat(eventEditorState.filters);
    }

    return {
      ...eventEditorState,
      filter: {
        type: "and",
        filters: filters,
      },
      source: eventSourceForEventType(eventEditorState.type),
      snapshots: eventEditorState.snapshots,
      segment_compatible: eventEditorState.segmentCompatible,
    };
  }

  // This updates the events array by replacing the event with the given id with
  // fn() applied to it.
  const updateEventDef = useCallback(
    function updateEventDef(eventDefID, fn) {
      const existingEventDef = events.find(
        (eventDef) => eventDef.id === eventDefID
      );
      const newEventDef = fn(existingEventDef);
      const eventDefs = events.filter((eventDef) => eventDef.id !== eventDefID);

      if (newEventDef) {
        var insertIndex = eventDefs.findIndex(
          (eventDef) =>
            newEventDef.name.toLowerCase() <= eventDef.name.toLowerCase()
        );

        if (
          insertIndex !== -1 &&
          newEventDef.name === eventDefs[insertIndex].name
        ) {
          eventDefs[insertIndex] = newEventDef;
        } else {
          if (insertIndex === -1) {
            insertIndex = eventDefs.length;
          }

          eventDefs.splice(insertIndex, 0, newEventDef);
        }
      }

      setEvents(eventDefs);
    },
    [events, setEvents]
  );

  function cssSelectorIsValid(selector) {
    try {
      document.querySelectorAll(selector);
      return true;
    } catch {
      return false;
    }
  }

  function isEventDefValid(eventDef) {
    if (eventDef.name.length === 0) {
      alert.error("The event definition has no name.");
      return false;
    }

    if (eventDef.filter && eventDef.filter.filters) {
      if (
        eventDef.filter.filters.find(
          (subFilter) =>
            subFilter.op === "css_matches" && !cssSelectorIsValid(subFilter.val)
        )
      ) {
        alert.error("The CSS selector you entered is not valid");
        return false;
      }
    }

    if (
      events.find(
        (otherEventDef) =>
          otherEventDef.name === eventDef.name &&
          otherEventDef.id !== eventDef.id
      )
    ) {
      alert.error(
        "Unable to save event definition. The event definition has the same name as another event."
      );
      return false;
    }

    if (eventEditorState.typeSpecificFilterVal === "") {
      if (eventEditorState.type === "pageview") {
        alert.error("The event definition does not have a URL filter.");
      } else if (eventEditorState.type === "react_navigation_screenview") {
        alert.error("The event definition does not have a screen view filter.");
      } else {
        alert.error(
          "The event definition does not have a CSS selector filter."
        );
      }
      return false;
    }

    for (var filter of eventEditorState.filters) {
      if (!filter.prop) {
        alert.error(
          "The property for one of the event definition filters is unselected."
        );
        return false;
      }

      if (!filter.op) {
        alert.error(
          "The operator for one of the event definition filters is unselected."
        );
        return false;
      }
    }

    for (var snapshot of eventEditorState.snapshots) {
      if (!(snapshot.name && snapshot.name !== "")) {
        alert.error("One of the dynamic properties does not have a name");
        return false;
      }
    }

    return true;
  }

  // This saves the current event in the event def editor to the backend and
  // updates the local state.

  // handleSaveEvent is a dependency of a useEffect later on. This causes the
  // useEffect to rerun on every refresh. This is ok based on how the useEffect
  // works.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  function handleSaveEvent() {
    const newEventDef = editorStateToEventDef();
    if (!isEventDefValid(newEventDef)) {
      return;
    }

    makeRequest("save_event_def", newEventDef)
      .then(() => {
        const newEventEntry = _.extend({}, newEventDef, {
          updated_at: new Date().toISOString(),
          enabled_dests: enabledDests,
        });
        updateEventDef(newEventEntry.id, () => newEventEntry);

        setUnsavedEdits(false);
        setEditorType("edit");
        handleClickEvent({ id: newEventEntry.id });
        alert.success("Event Saved");
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save event.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  const handleToggleDestination = useCallback(
    function handleToggleDestination(destID) {
      if (unsavedEdits) {
        alert.error("You have unsaved event definition changes.");
        return;
      }

      const newDestObj = {};
      const newState = !enabledDests[destID];
      newDestObj[destID] = newState;

      makeRequest("toggle_destination", {
        dest_id: destID,
        event_def_id: eventEditorState.id,
        state: newState,
      })
        .then(() => {
          const newEnabledDests = _.extend({}, enabledDests, newDestObj);
          setEnabledDests(newEnabledDests);
          updateEventDef(eventEditorState.id, (eventDef) => {
            return _.extend({}, eventDef, { enabled_dests: newEnabledDests });
          });
        })
        .catch((error) => {
          alert.error("Something went wrong. Unable to toggle destination.");
          console.log(error);
          Sentry.captureException(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [unsavedEdits, enabledDests, eventEditorState.id, alert, updateEventDef]
  );

  const handleBackfill = useCallback(
    function handleBackfill(destID) {
      if (unsavedEdits) {
        alert.error("You have unsaved event definition changes.");
        return;
      }

      setDisabledBackfills(_.extend({}, disabledBackfills, { [destID]: true }));

      makeRequest("backfill_event_def", {
        event_def_id: eventEditorState.id,
        dest_id: destID,
      })
        .then(() => alert.success("Backfill Enqueued"))
        .catch((error) => {
          alert.error("Something went wrong. Unable to enqueue backfill.");
          console.log(error);
          setDisabledBackfills(
            _.extend({}, disabledBackfills, { [destID]: false })
          );
          Sentry.captureException(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [alert, eventEditorState.id, unsavedEdits, disabledBackfills]
  );

  const handleEditorStateChange = useCallback(function handleEditorStateChange(
    fn
  ) {
    setUnsavedEdits(true);
    setEventEditorState(fn);
  },
  []);

  const handleCloseDeleteEventModal = useCallback(
    function handleCloseDeleteEventModal() {
      setDeleteEventModalOpen(false);
    },
    []
  );

  const handleOpenDestinationsSettingModal = useCallback(
    function handleOpenDestinationsSettingModal() {
      if (unsavedEdits) {
        alert.error("You have unsaved event definition changes.");
        return;
      }

      setDestinationSettingsModalOpen(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [alert, unsavedEdits]
  );

  const handleCloseDestinationSettingsModal = useCallback(
    function handleCloseDestinationSettingsModal() {
      setDestinationSettingsModalOpen(false);
    },
    []
  );

  const handleSaveDestinationSettingsModal = useCallback(
    function handleSaveDestinationSettingsModal(settings) {
      setEventEditorState((oldState) => {
        const newState = _.extend({}, oldState, settings);
        return newState;
      });

      setTriggerSaveEvent(true);
    },
    []
  );

  // After toggling segment compatibility we immediately save the event
  // definition.
  useEffect(
    function () {
      if (triggerSaveEvent) {
        handleSaveEvent();
        setTriggerSaveEvent(false);
      }
    },
    [triggerSaveEvent, handleSaveEvent]
  );

  // Handle URL param changes
  useEffect(() => {
    if (urlParams.eventDefID === "new") {
      // Creating one
      const queryParams = new URLSearchParams(location.search);
      handleNewEventDefinition(
        queryParams.get("type") || undefined,
        queryParams.get("typeSpecificProperty") || undefined
      );
    } else if (urlParams.eventDefID) {
      // Editing one
      const foundEvent = events.find((evt) => evt.id === urlParams.eventDefID);
      if (foundEvent) {
        handleSelectEvent(foundEvent);
      }
    } else {
      // Nothing.
      setEditorType(null);
      setEnabledDests(null);
      setDisabledBackfills(null);
      setEventEditorState({});
      setUnsavedEdits(false);
    }
  }, [
    urlParams,
    urlParams.eventDefID,
    handleNewEventDefinition,
    events,
    handleSelectEvent,
    location.search,
    location,
  ]);

  useEffect(() => {
    dispatch(fetchEventDefs());
  }, [dispatch]);

  onEnvironmentChange(() => {
    // When the environment changes, go back to the initial page
    history.replace(EVENTS_PAGE_BASE);
  });

  const handleDeleteEvent = useCallback(
    function handleDeleteEvent() {
      makeRequest("delete_event", { event_def_id: eventEditorState.id })
        .then(() => {
          setEditorType(null);
          setEnabledDests(null);
          setDisabledBackfills(null);
          setEventEditorState({});
          setUnsavedEdits(false);
          updateEventDef(eventEditorState.id, () => null);
          alert.success("Event Deleted");
          history.replace(EVENTS_PAGE_BASE);
        })
        .catch((error) => {
          alert.error("Something went wrong. Unable to delete event.");
          console.log(error);
          Sentry.captureException(error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [alert, eventEditorState.id, history, updateEventDef]
  );

  const isCustomEvent = isCustomEventType(eventEditorState.type);

  return (
    <>
      {!eventDefsLoading && (
        <>
          <EventsTopbar
            searchString={searchString}
            onSearchStringChange={setSearchString}
            onNewEventDef={handleClickNewEventDef}
            editorState={eventEditorState}
            unsavedEdits={unsavedEdits}
          />
          <div className={classes.content}>
            <EventList
              activeEvent={eventEditorState.id}
              events={events}
              searchString={searchString}
              onSelect={handleClickEvent}
            />
            <Grid
              container
              spacing={0}
              alignItems="flex-start"
              alignContent="flex-start"
              className={clsx(classes.widgets, "px-5 pb-2")}
            >
              {destinations && editorType && (
                <>
                  <Grid item xs={8}>
                    <div className={classes.widget}>
                      {isCustomEvent ? (
                        <CustomEventDisplay editorState={eventEditorState} />
                      ) : (
                        <EventEditor
                          editorState={eventEditorState}
                          editorType={editorType}
                          setState={handleEditorStateChange}
                          onSave={handleSaveEvent}
                          onDelete={() => setDeleteEventModalOpen(true)}
                        />
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={8}>
                    <div className={classes.widget}>
                      <EventDestinations
                        eventIsServerSide={
                          libraryForEventType(eventEditorState.type) !== "web"
                        }
                        eventIsCustom={isCustomEvent}
                        destinations={destinations}
                        enabledDests={enabledDests}
                        disabledBackfills={disabledBackfills}
                        onToggleDestination={handleToggleDestination}
                        onBackfillDestination={handleBackfill}
                        openDestinationsSettingModal={
                          handleOpenDestinationsSettingModal
                        }
                        segmentCompatible={
                          eventEditorState && eventEditorState.segmentCompatible
                        }
                      />
                    </div>
                  </Grid>
                </>
              )}
              {editorType === null && events && events.length === 0 && (
                <Grid item>
                  <EmptyState
                    onNewEventDef={() => handleNewEventDefinition()}
                  />
                </Grid>
              )}
            </Grid>
          </div>
        </>
      )}
      <DeleteEventModal
        open={deleteEventModalOpen}
        onClose={handleCloseDeleteEventModal}
        onConfirm={handleDeleteEvent}
      />
      <DestinationSettingsModal
        open={destinationSettingsModalOpen}
        onClose={handleCloseDestinationSettingsModal}
        onSave={handleSaveDestinationSettingsModal}
        settings={{
          segmentCompatible:
            eventEditorState && eventEditorState.segmentCompatible,
        }}
      />
    </>
  );
}

const useEventListStyles = makeStyles((theme) => ({
  eventListPaper: {
    height: "100%",
    width: 275,
    overflowY: "auto",
    flexShrink: 0,
    borderRadius: 0,
  },
  eventList: {
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  eventTypeHeader: {
    backgroundColor: "white",
    color: "#617984",
  },
  headerText: {
    "& span": {
      color: "#617984",
      fontWeight: "bold",
      textTransform: "uppercase",
      fontSize: 12,
    },
  },
  triangle: {
    borderTop: "4px solid #4050B5",
    borderLeft: "4px solid transparent",
    borderRight: "4px solid transparent",
    width: 0,
    height: 0,
    transition: ".5s all",
  },
  triangleOpen: {
    transform: "rotate(180deg)",
  },
  listItem: {
    borderBottom: "1px solid #eeeeee",
    overflow: "hidden",
    transition: "flex .5s ease-in-out",
  },
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
    fill: "#617984",
  },
  active: {
    backgroundColor: "#C5CAE9",
    fontWeight: 550,
    "&:hover": {
      backgroundColor: "#C5CAE9",
    },
  },
  hidden: {
    visibility: "hidden",
  },
}));

// Renders the list of events. We do the string searching inside of EventList so
// React.memo will work. If the events object changes every call, React.memo
// will rerender this component.
const EventList = React.memo(function EventList({
  activeEvent,
  events,
  onSelect,
  searchString,
}) {
  const classes = useEventListStyles();

  events =
    events &&
    events.filter((event) =>
      event.name.toLowerCase().includes(searchString.toLowerCase())
    );

  const invisibleEvents = events
    ? events.filter((event) => !allEventTypes.includes(eventDefType(event)))
    : [];
  for (var i = 0; i < invisibleEvents.length; i++) {
    Sentry.captureException(
      new Error(`Event ${invisibleEvents[i].id} not showing in UI`)
    );
  }

  const renderedCategories = eventCategories.map((category) => {
    return (
      <EventListCategory
        key={category.name}
        name={category.name}
        events={events}
        type={category.type}
        activeEvent={activeEvent}
        onSelect={onSelect}
      />
    );
  });

  return (
    <Paper
      className={clsx(
        classes.eventListPaper,
        (!events || events.length === 0) && classes.hidden
      )}
    >
      <List disablePadding className={clsx(classes.eventList)}>
        {renderedCategories}
      </List>
    </Paper>
  );
});

EventList.propTypes = {
  activeEvent: PropTypes.string,
  events: PropTypes.array,
  onSelect: PropTypes.func,
  searchString: PropTypes.string,
};

// EventListCategory renders an event list for one category of event.
function EventListCategory({ name, events, type, activeEvent, onSelect }) {
  const classes = useEventListStyles();
  const Icon = iconForEventType(type);

  const [isOpen, setIsOpen] = useLocalStorage(
    `events/shema/category/${type}/`,
    true
  );

  events = events && events.filter((event) => eventDefType(event) === type);

  if (_.isEmpty(events)) {
    return null;
  }

  return (
    <>
      <ListItem
        divider
        className={clsx(classes.eventTypeHeader, "cursor-pointer")}
        onClick={() => setIsOpen(!isOpen)}
      >
        {Icon && <Icon height={20} width={20} className={classes.icon} />}
        <ListItemText
          classes={{ root: clsx(classes.headerText) }}
          primary={name}
        />
        <div
          className={clsx(classes.triangle, isOpen && classes.triangleOpen)}
        />
      </ListItem>
      <AnimateHeight duration={500} height={isOpen ? "auto" : 0}>
        {events.map((event) => {
          return (
            <ListItem
              className={clsx(
                classes.listItem,
                activeEvent === event.id && classes.active
              )}
              key={event.id}
              button
              onClick={() => onSelect(event)}
            >
              <ListItemText
                primary={event.name}
                className="truncate"
                title={event.name}
              />
            </ListItem>
          );
        })}
      </AnimateHeight>
    </>
  );
}

EventListCategory.propTypes = {
  name: PropTypes.string.isRequired,
  events: PropTypes.array,
  type: PropTypes.string.isRequired,
  activeEvent: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

const useEventEditorStyles = makeStyles((theme) => ({
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
  filterRegion: {
    display: "flex",
    justifyContent: "flex-end",
    alignContent: "center",
  },
  createEventButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.green[600],
    "&:hover": {
      backgroundColor: colors.green[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.green[600],
      },
    },
  },
  deleteEventButton: {
    color: colors.red[600],
    border: `1px solid ${fade(colors.red[600], 0.5)}`,
    "&:hover": {
      border: `1px solid ${colors.red[600]}`,
      backgroundColor: fade(colors.red[600], theme.palette.action.hoverOpacity),
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  eventActions: {
    display: "flex",
    justifyContent: "space-between",
  },
  hidden: {
    visibility: "hidden",
  },
}));

function shallowEqual(a, b) {
  return _.isEqualWith(a, b, (x, y) => {
    if (!_.isObject(x) && !_.isObject(y)) {
      return x === y;
    }

    return undefined;
  });
}

const MemoTextField = React.memo(TextField, (a, b) => {
  return shallowEqual(_.omit(a, "children"), _.omit(b, "children"));
});

function CustomEventDisplay({ editorState }) {
  return (
    <Card>
      <CardHeader title={"Event Name"} />
      <Divider />
      <CardContent>
        <div style={{ paddingLeft: "15px" }}>
          <Typography variant="h5">{editorState.name}</Typography>
        </div>
      </CardContent>
    </Card>
  );
}

CustomEventDisplay.propTypes = {
  editorState: PropTypes.object.isRequired,
};

// This is there is only a single instance of this object. This allows us to use
// memoization for the MemoTextField when passing in the shrink argument.
const shrink = { shrink: true };
const native = { native: true };

function EventEditor({ editorState, editorType, setState, onSave, onDelete }) {
  const classes = useEventEditorStyles();

  const handlePropertyChange = useCallback(
    function handlePropertyChange(prop, value) {
      setState((editorState) => {
        const newEditorState = _.extend({}, editorState, { [prop]: value });
        return newEditorState;
      });
    },
    [setState]
  );

  const setName = useCallback(
    function setName(e) {
      handlePropertyChange("name", e.target.value);
    },
    [handlePropertyChange]
  );

  const setType = useCallback(
    function setType(e) {
      handlePropertyChange("type", e.target.value);
    },
    [handlePropertyChange]
  );

  const setTypeSpecificFilterVal = useCallback(
    function setTypeSpecificFilterVal(value) {
      handlePropertyChange("typeSpecificFilterVal", value);
    },
    [handlePropertyChange]
  );

  const setLibrary = useCallback(
    function setLibrary(e) {
      // We determine the library in use based on the event type. When you toggle
      // a new library, we just change the current event type to one from the
      // designated library.
      if (e.target.value === "web") {
        handlePropertyChange("type", "click");
      } else if (e.target.value === "react-native") {
        handlePropertyChange("type", "touch");
      }
    },
    [handlePropertyChange]
  );

  function handleAddFilter() {
    setState((editorState) => {
      const newFilter = {
        type: "prop_filter",
        prop: "",
        op: "",
        val: "",
      };

      const newFilters = editorState.filters.concat([newFilter]);
      const newState = _.extend({}, editorState, { filters: newFilters });
      return newState;
    });
  }

  function handleAddSnapshot() {
    setState((editorState) => {
      const newSnapshot = {
        name: "",
        css_selector: "",
      };

      const newSnapshots = editorState.snapshots.concat([newSnapshot]);
      const newState = _.extend({}, editorState, { snapshots: newSnapshots });
      return newState;
    });
  }

  const setFilterListState = useCallback(
    function setFilterListState(updateFiltersFn) {
      setState((editorState) => {
        const newFilters = updateFiltersFn(editorState.filters);
        const newState = _.extend({}, editorState, { filters: newFilters });
        return newState;
      });
    },
    [setState]
  );

  const setSnapshotListState = useCallback(
    function setSnapshotListState(updateSnapshotsFn) {
      setState((editorState) => {
        const newSnapshots = updateSnapshotsFn(editorState.snapshots);
        const newState = _.extend({}, editorState, { snapshots: newSnapshots });
        return newState;
      });
    },
    [setState]
  );

  var typeSpecificFilterName;
  var typeSpecificFilterProp;

  if (editorState.type === "pageview") {
    typeSpecificFilterName = "URL";
    typeSpecificFilterProp = "$pathname";
  } else if (editorState.type === "react_navigation_screenview") {
    typeSpecificFilterName = "Screen View Route";
    typeSpecificFilterProp = "$screen_path";
  } else {
    typeSpecificFilterName = "CSS Selector";
    typeSpecificFilterProp = null;
  }

  return (
    <Card>
      <CardHeader
        title={editorType === "create" ? "Create A New Event" : "Edit Event"}
        action={
          <div aria-label="info" className="text-right text-xs text-grey-600">
            {editorState.created_at && (
              <>
                <span className="font-bold mr-2">Created:</span>
                {new Date(editorState.created_at).toLocaleString()}
                <br />
              </>
            )}

            {editorState.updated_at && (
              <>
                <span className="font-bold mr-2">Updated:</span>
                {new Date(editorState.updated_at).toLocaleString()}
              </>
            )}
          </div>
        }
      />
      <Divider />
      <CardContent>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              label="Event Name"
              name="name"
              onChange={setName}
              value={editorState.name}
            />
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              label="Library"
              name="library"
              onChange={setLibrary}
              select
              SelectProps={native}
              value={libraryForEventType(editorState.type)}
              variant="outlined"
            >
              <option value="web">Web</option>
              <option value="react-native">React Native</option>
            </MemoTextField>
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              label="Event Type"
              name="event-type"
              onChange={setType}
              select
              SelectProps={native}
              value={editorState.type}
              variant="outlined"
            >
              {optionsForLibrary(libraryForEventType(editorState.type))}
            </MemoTextField>
          </Grid>

          <Grid item xs={12}>
            <AutocompleteField
              propName={typeSpecificFilterProp}
              fullWidth
              autoComplete="off"
              variant="outlined"
              InputLabelProps={shrink}
              label={typeSpecificFilterName}
              name="type-specific-filter-val"
              onChange={setTypeSpecificFilterVal}
              value={editorState.typeSpecificFilterVal}
            />
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      <CardContent>
        <FilterList
          library={libraryForEventType(editorState.type)}
          filters={editorState.filters}
          setState={setFilterListState}
        />
        <div className={classes.filterRegion}>
          <Button color="default" onClick={handleAddFilter} variant="outlined">
            <AddIcon className={classes.icon} />
            Add Filter
          </Button>
        </div>
      </CardContent>
      <Divider />
      <CardContent>
        <SnapshotList
          snapshots={editorState.snapshots}
          setState={setSnapshotListState}
        />
        <div className={classes.filterRegion}>
          <Button
            color="default"
            onClick={handleAddSnapshot}
            variant="outlined"
          >
            <AddIcon className={classes.icon} />
            Add Dynamic Property
          </Button>
        </div>
      </CardContent>
      <Divider />
      <CardContent className={classes.eventActions}>
        <Button
          className={clsx(
            classes.deleteEventButton,
            editorType === "create" && classes.hidden
          )}
          onClick={onDelete}
        >
          Delete
        </Button>

        <Button
          className={classes.createEventButton}
          onClick={onSave}
          variant="outlined"
        >
          {editorType === "create" ? "Create Event" : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}

EventEditor.propTypes = {
  editorState: PropTypes.object.isRequired,
  editorType: PropTypes.string.isRequired,
  setState: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function SnapshotList({ snapshots, setState }) {
  const handleSnapshotChange = useCallback(
    function handleSnapshotChange(index, snapshot) {
      setState((snapshots) => {
        const newList = snapshots.slice();
        newList[index] = snapshot;
        return newList;
      });
    },
    [setState]
  );

  const handleSnapshotDeletion = useCallback(
    function handleSnapshotDeletion(index) {
      setState((snapshots) => {
        const newSnapshots = snapshots.slice();
        newSnapshots.splice(index, 1);
        return newSnapshots;
      });
    },
    [setState]
  );

  const renderedSnapshots = snapshots.map((snapshot, index) => {
    return (
      <Snapshot
        key={index}
        index={index}
        snapshot={snapshot}
        onChange={handleSnapshotChange}
        onDelete={handleSnapshotDeletion}
      />
    );
  });

  return <>{renderedSnapshots}</>;
}

SnapshotList.propTypes = {
  snapshots: PropTypes.array.isRequired,
  setState: PropTypes.func.isRequired,
};

const snapshotStyles = makeStyles(() => ({
  snapshot: {
    marginBottom: 16,
  },
  closeIcon: {
    alignSelf: "center",
    "&:hover": {
      cursor: "pointer",
    },
  },
  bold: {
    fontWeight: 550,
  },
}));

const Snapshot = React.memo(function Snapshot({
  snapshot,
  index,
  onChange,
  onDelete,
}) {
  const classes = snapshotStyles();

  function handlePropertyChange(prop, value) {
    const newSnapshot = _.extend({}, snapshot, { [prop]: value });
    onChange(index, newSnapshot);
  }

  return (
    <Grid container spacing={1} className={classes.snapshot}>
      <Grid item xs={4}>
        <MemoTextField
          fullWidth
          autoComplete="off"
          label="Property Name"
          name="dynamic-property-name"
          onChange={(e) => handlePropertyChange("name", e.target.value)}
          InputLabelProps={{ shrink: true }}
          value={snapshot.name}
          variant="outlined"
        />
      </Grid>
      <Grid item xs={7}>
        <AutocompleteField
          propName={snapshot.prop}
          fullWidth
          label="CSS Selector"
          name="selector"
          onChange={(value) => handlePropertyChange("css_selector", value)}
          value={snapshot.css_selector}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
        />
      </Grid>
      <Grid className={clsx(classes.closeIcon)} item xs={1}>
        <CloseIcon onClick={() => onDelete(index)} />
      </Grid>
    </Grid>
  );
});

Snapshot.propTypes = {
  snapshot: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const useEventDestinationsStyles = makeStyles((theme) => ({
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
  smallIcon: {
    display: "flex",
    marginRight: theme.spacing(1) / 2,
  },
  content: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  backfillButton: {
    paddingRight: 10,
    paddingLeft: 10,
  },
  destinationSettingsButton: {
    display: "flex",
    alignItems: "center",
    marginTop: 0,
    "&:hover": {
      cursor: "pointer",
    },
  },
  colorBlue: {
    color: "blue",
  },
  bold: {
    fontWeight: 550,
  },
  noMarginTop: {
    marginTop: 0,
  },
  destinationInfo: {
    display: "flex",
    alignItems: "center",
  },
  destinationName: {
    color: "#444",
    fontSize: 16,
    verticalAlign: "center",
    marginLeft: theme.spacing(2),
  },
}));

function BackfillButton({
  dest,
  destinationDefinition,
  classes,
  onBackfillDestination,
  disabled,
}) {
  if (!destinationSupportsBackfill(dest.name) || !dest.is_valid_server_side) {
    return null;
  }

  if (destinationDefinition.type === "warehouse") {
    return (
      <Tooltip title="Warehouse destinations are automatically backfilled">
        <div>
          <Button
            color="primary"
            variant="outlined"
            disabled
            className={classes.backfillButton}
          >
            <UploadToCloudIcon className={classes.icon} />
            BACKFILL
          </Button>
        </div>
      </Tooltip>
    );
  }

  return (
    <Button
      color="primary"
      variant="outlined"
      className={classes.backfillButton}
      onClick={() => onBackfillDestination(dest.id)}
      disabled={disabled}
    >
      <UploadToCloudIcon className={classes.icon} />
      BACKFILL
    </Button>
  );
}

BackfillButton.propTypes = {
  dest: PropTypes.object.isRequired,
  destinationDefinition: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  onBackfillDestination: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const EventDestinations = React.memo(function EventDestinations({
  eventIsServerSide,
  eventIsCustom,
  destinations,
  enabledDests,
  disabledBackfills,
  onToggleDestination,
  onBackfillDestination,
  openDestinationsSettingModal,
  segmentCompatible,
}) {
  const classes = useEventDestinationsStyles();

  if (!destinations) {
    return null;
  }

  const renderedDestinations = destinations
    .filter((dest) => {
      return (
        dest.enabled && destinationIsValidForEvent(dest, eventIsServerSide)
      );
    })
    .map((dest) => {
      const destinationSlug = destinationKeyToSlugMap[dest.name];
      const destinationDefinition = allDestinationsData[destinationSlug];
      const DestinationIcon = destinationDefinition.logoSquare;

      return (
        <TableRow key={dest.id}>
          <TableCell>
            <div className={classes.destinationInfo}>
              {DestinationIcon && <DestinationIcon height={34} width={34} />}
              <span className={classes.destinationName}>
                {destinationDefinition.name}
              </span>
            </div>
          </TableCell>

          <TableCell align="center">
            <Switch
              checked={
                (segmentCompatible && !destinationIsWarehouse(dest.name)) ||
                !!enabledDests[dest.id]
              }
              color="secondary"
              name="enable-destination-for-event"
              onChange={() => onToggleDestination(dest.id)}
              value={segmentCompatible || !!enabledDests[dest.id]}
              disabled={segmentCompatible && !destinationIsWarehouse(dest.name)}
            />
          </TableCell>

          <TableCell align="center">
            <BackfillButton
              dest={dest}
              destinationDefinition={destinationDefinition}
              classes={classes}
              onBackfillDestination={onBackfillDestination}
              disabled={!!disabledBackfills[dest.id]}
            />
          </TableCell>
        </TableRow>
      );
    });

  return (
    <Card>
      <CardHeader
        title="Event Destinations"
        classes={{ action: clsx(classes.noMarginTop) }}
        action={
          eventIsCustom && (
            <div
              className={clsx(classes.destinationSettingsButton)}
              onClick={() => openDestinationsSettingModal()}
            >
              <SettingsIcon className={clsx(classes.smallIcon)} />
              <Typography component="h1">
                <span className={clsx(classes.colorBlue, classes.bold)}>
                  SETTINGS
                </span>
              </Typography>
            </div>
          )
        }
      />
      <Divider />
      <CardContent className={classes.content}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Destination</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Backfill</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderedDestinations}</TableBody>
        </Table>
      </CardContent>
    </Card>
  );
});

EventDestinations.propTypes = {
  eventIsServerSide: PropTypes.bool.isRequired,
  eventIsCustom: PropTypes.bool.isRequired,
  destinations: PropTypes.array,
  enabledDests: PropTypes.object,
  disabledBackfills: PropTypes.object,
  onToggleDestination: PropTypes.func,
  onBackfillDestination: PropTypes.func,
  openDestinationsSettingModal: PropTypes.func.isRequired,
  segmentCompatible: PropTypes.bool.isRequired,
};

const useEmptyStateClasses = makeStyles(() => ({
  container: {
    paddingTop: 150,
    paddingLeft: 180,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  paragraph: {
    width: 540,
    margin: 20,
  },
}));

// This shows what to render when no events have been created yet.
function EmptyState({ onNewEventDef }) {
  const classes = useEmptyStateClasses();

  return (
    <div className={clsx(classes.container)}>
      <img
        className="mb-4"
        alt="Man creating event schemas"
        src={require("./img/events_empty_state.svg")}
        height={201}
        width={273}
      />
      <Typography component="h1" gutterBottom variant="h3">
        You haven&apos;t created any events yet
      </Typography>
      <Typography component="p" className={classes.paragraph}>
        If you&apos;ve installed the Autotrack snippet, Freshpaint is already
        collecting every interaction. Create events in Freshpaint to describe
        certain behaviors on your site or app, and send autotracked data to your
        Destinations.
      </Typography>
      <Button color="primary" onClick={onNewEventDef} variant="contained">
        <AddIcon className={classes.icon} />
        Create New Event
      </Button>
    </div>
  );
}

EmptyState.propTypes = {
  onNewEventDef: PropTypes.func,
};

const useModalStyles = makeStyles((theme) => ({
  root: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    boxShadow: theme.shadows[20],
    width: 500,
    maxHeight: "100%",
    overflowY: "auto",
    maxWidth: "100%",
  },
  container: {
    marginTop: theme.spacing(3),
    height: 200,
  },
  actions: {
    justifyContent: "flex-end",
  },
  inputField: {
    marginTop: 16,
  },
  deactivateButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.red[600],
    "&:hover": {
      backgroundColor: colors.red[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.red[600],
      },
    },
  },
  closeIconAction: {
    "&:hover": {
      cursor: "pointer",
    },
    margin: 0,
  },
  bold: {
    fontWeight: 550,
  },
}));

const DeleteEventModal = React.memo(function DeleteEventModal({
  open,
  onClose,
  onConfirm,
}) {
  const classes = useModalStyles();

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={"Delete Event"}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">
            This is going to affect any Destinations you are sending this event
            to, but will not delete any data from Freshpaint.
            <br />
            <br />
            <span className={clsx(classes.bold)}>
              Are you sure you want to delete this event?
            </span>
          </Typography>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={onClose}>CANCEL</Button>
          <Button
            className={clsx(classes.deactivateButton)}
            onClick={() => {
              onConfirm();
              onClose();
            }}
            variant="contained"
          >
            DELETE EVENT
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
});

DeleteEventModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  dest: PropTypes.object,
};

const DestinationSettingsModal = React.memo(function DestinationSettingsModal({
  open,
  onClose,
  onSave,
  settings,
}) {
  const classes = useModalStyles();

  const [settingsToUpdate, setSettingsToUpdate] = useState({});

  if (!open) {
    return null;
  }

  function addSetting(key, value) {
    const newSettingsToUpdate = _.extend({}, settingsToUpdate, {
      [key]: value,
    });
    setSettingsToUpdate(newSettingsToUpdate);
  }

  function lookupSetting(key) {
    if (key in settingsToUpdate) {
      return settingsToUpdate[key];
    }

    return settings[key];
  }

  function handleSave() {
    onClose();
    onSave(settingsToUpdate);
    setSettingsToUpdate({});
  }

  function handleClose() {
    onClose();
    setSettingsToUpdate({});
  }

  return (
    <Modal onClose={onClose} open={open}>
      <Card className={clsx(classes.root)}>
        <CardHeader
          title={"Event Destinations: Advanced Settings"}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={9}>
              <Typography variant="body1">
                <span className={clsx(classes.bold)}>
                  Override hardcoded destinations?
                </span>
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Switch
                checked={!lookupSetting("segmentCompatible")}
                color="secondary"
                name="enable-destination-for-event"
                onChange={() =>
                  addSetting(
                    "segmentCompatible",
                    !lookupSetting("segmentCompatible")
                  )
                }
                value={!lookupSetting("segmentCompatible")}
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={() => handleClose()}>CLOSE</Button>

          <Button
            onClick={() => handleSave()}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
});

DestinationSettingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  settings: PropTypes.object.isRequired,
};

const navigationStyles = makeStyles(() => ({
  navigation: {
    paddingLeft: 0,
    paddingRight: 0,
    height: "100%",
    display: "flex",
    flexFlow: "column",
  },
}));

function EventsPageWithNavigation() {
  const classes = navigationStyles();

  return (
    <Navigation className={classes.navigation}>
      <EventsPage />
    </Navigation>
  );
}

export default EventsPageWithNavigation;
